/**
 * A transaction code recorded before (409 `DUPLICATE_REFERENCE`, owner decision 2026-10-05 §3):
 * the money already recorded with it, and the acknowledgement staff tick after checking it is not
 * the same money. Nothing is credited until they do.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { isApiError, type DecimalString } from '../../api';
import { Callout } from '../../components/blocks';
import { formatDateTime } from '../../lib/format';
import { ConfirmCheck } from '../compliance/ui';
import { bankTxPath } from './BankTransactionsPage';
import { Amount, journalPath } from './shared';

interface ReferenceUse {
  kind: 'BANK_TRANSACTION' | 'WALLET_ADJUSTMENT';
  /** The bank transaction, or the adjustment's journal entry. */
  id: string;
  amount: DecimalString;
  at: string;
}

/** The matches of a `DUPLICATE_REFERENCE` refusal, or null for any other outcome. */
export function duplicateMatches(error: unknown): ReferenceUse[] | null {
  if (!isApiError(error) || error.code !== 'DUPLICATE_REFERENCE') return null;
  const matches = (error.details as { matches?: unknown } | null | undefined)?.matches;
  return Array.isArray(matches) ? (matches as ReferenceUse[]) : [];
}

export function DuplicateReference({ matches, currency, checked, onChange }: { matches: ReferenceUse[]; currency: string; checked: boolean; onChange: (v: boolean) => void }) {
  const { t } = useTranslation('commerce');
  return (
    <div className="grid gap-2">
      <Callout tone="warn" title={t('duplicateRef.found', { count: matches.length })}>
        <ul className="list-disc pl-5">
          {matches.map((m) => (
            <li key={`${m.kind}:${m.id}`}>
              <Link to={m.kind === 'BANK_TRANSACTION' ? bankTxPath(m.id) : journalPath(m.id)} className="text-primary hover:underline">
                {t(`duplicateRef.kind.${m.kind}`)}
              </Link>{' '}
              · <Amount amount={m.amount} currency={currency} /> · {formatDateTime(m.at)}
            </li>
          ))}
        </ul>
      </Callout>
      <ConfirmCheck checked={checked} onChange={onChange}>
        {t('duplicateRef.acknowledge')}
      </ConfirmCheck>
    </div>
  );
}
